/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Grid } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Calendar } from 'lucide-react';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';
import DatePicker from '../../components/datePicker';
import ProjectSelector from '../../components/selectors/ProjectSelector';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import { FALLBACK_DEPARTMENTS, FALLBACK_DESIGNATIONS } from '../../constants/memberFallbacks';

const TEXT_INPUT_CLASS = 'w-full h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 focus:outline-none focus:border-brand-500';

// DS appearance for the shared PlacesAutocomplete's MUI outlined input.
const PLACES_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e0e1e6' },
    '&:hover fieldset': { borderColor: '#e0e1e6' },
    '&.Mui-focused fieldset': { borderColor: '#3d5a8a', borderWidth: '1px', boxShadow: 'none' },
  },
};

function FieldLabel({ children }) {
  return <span className="block text-sm font-medium text-ink-900 mb-1.5">{children}</span>;
}
FieldLabel.propTypes = { children: PropTypes.node.isRequired };

// The design-system green calendar caret used across all date pickers.
function CalendarCaret(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Calendar {...props} size={16} color="#53A551" />;
}

// Convert employment start date to workspace timezone
const getInitialEmploymentStartDate = (employmentStartDate) => {
  if (!employmentStartDate) return new Date();
  // Convert the date to the workspace timezone for correct display
  // const timezone = workspaceTimezone || 'UTC';
  return moment(employmentStartDate).toDate();
};

function EditMemberModal({
  member, closeModal, getMembers, reportingManagers, departments, designations,
}) {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [formData, setFormData] = useState({
    firstName: member.user.firstName,
    lastName: member.user.lastName,
    email: member.user.email,
    timezone: member.user.timezone,
    accountType: member.accountType,
    department: member.department,
    designation: member.designation,
    employmentStartDate: getInitialEmploymentStartDate(
      member.employmentStartDate,
      activeWorkspace?.timezone,
    ),
    latlng: {
      latitude: member.user.userLocation?.latitude,
      longitude: member.user.userLocation?.longitude,
    },
    location: {
      description: member.user.userLocation?.formattedAddress,
    },
    reportingManager: member.reportingManager,
  });
  // eslint-disable-next-line
  const [formErrors, setFormErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [memberProjects, setMemberProjects] = useState([]);

  const isUpdatable = () => {
    const originalProjectIds = (memberProjects || []).map((mp) => mp.projectId).sort();
    const selectedProjectIds = (formData.selectedProjects || []).map((p) => p.id).sort();
    const projectsChanged = JSON.stringify(originalProjectIds) !== JSON.stringify(selectedProjectIds);

    // Compare dates by converting formData date to string for comparison
    const formDataDateStr = formData.employmentStartDate
      ? moment(formData.employmentStartDate).format('YYYY-MM-DD')
      : null;
    const memberDateStr = member.employmentStartDate
      ? moment(member.employmentStartDate).format('YYYY-MM-DD')
      : null;
    const dateChanged = formDataDateStr !== memberDateStr;

    return formData.accountType !== member.accountType
      || formData.department !== member.department
      || formData.designation !== member.designation
      || formData.latlng?.latitude !== member.user.userLocation?.latitude
      || formData.latlng?.longitude !== member.user.userLocation?.longitude
      || formData.location?.description !== member.user.userLocation?.formattedAddress
      || formData.reportingManager !== member.reportingManager
      || dateChanged
      || projectsChanged;
  };

  const handleForm = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProjectSelection = (selectedProjects) => {
    setFormData({ ...formData, selectedProjects });
  };

  const handleEditMember = async () => {
    setFormErrors({});
    setIsUpdating(true);

    try {
      const updateData = {
        email: typeof formData.email === 'string' ? formData.email.trim() : formData.email,
        accountType: typeof formData.accountType === 'string'
          ? formData.accountType.trim() : formData.accountType,
        department: typeof formData.department === 'string'
          ? (formData.department.trim() || null) : (formData.department || null),
        designation: typeof formData.designation === 'string'
          ? (formData.designation.trim() || null) : (formData.designation || null),
        reportingManager: formData.accountType === 'Primary owner'
          ? null
          : (formData.reportingManager || null),
        employmentStartDate: formData.employmentStartDate,
        latlng: formData.latlng,
        location: formData.location,
      };

      const memberResponse = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.editMember(activeWorkspace.id, member.id),
        data: { updateData },
      });

      if (memberResponse.success) {
        try {
          const selectedProjectIds = (formData.selectedProjects || []).map((project) => project.id);

          const projectUpdateResponse = await sendRequest({
            method: 'put',
            url: API.WORKSPACE.updateMemberProjects(activeWorkspace.id, member.id),
            data: { projectIds: selectedProjectIds },
          });

          if (projectUpdateResponse.success) {
            const totalProjects = selectedProjectIds.length;
            const message = totalProjects > 0
              ? `Member updated and assigned to ${totalProjects} project(s) successfully!`
              : 'Member updated successfully!';

            displayNotification({ message, severity: 'success' });

            await getMembers();

            closeModal();
          } else {
            displayNotification({ message: 'Failed to update member projects. Please try again.', severity: 'error' });
          }
        } catch (projectError) {
          displayNotification({ message: 'Failed to update member projects. Please try again.', severity: 'error' });
        }
      } else {
        displayNotification({ message: 'Failed to update member. Please try again.', severity: 'error' });
      }
    } catch (error) {
      displayNotification({ message: 'An unexpected error occurred. Please try again.', severity: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const loadProjectsAndMemberProjects = async () => {
      if (activeWorkspace && member) {
        setProjectsLoading(true);

        try {
          const projectsResponse = await sendRequest({
            method: 'get',
            url: API.WORKSPACE.getProjects(activeWorkspace.id),
          });

          let allProjects = [];
          if (projectsResponse.success && projectsResponse.data?.data
            && Array.isArray(projectsResponse.data.data)) {
            allProjects = projectsResponse.data.data.map((project) => ({
              id: project.id,
              name: project.name || `Project ${project.id}`,
              description: project.description || '',
              workspaceId: project.workspaceId,
              ...project,
            }));
          }

          setProjects(allProjects);

          try {
            const memberProjectsResponse = await sendRequest({
              method: 'get',
              url: API.WORKSPACE.getMemberProjects(activeWorkspace.id, member.id),
            });

            if (memberProjectsResponse.success && memberProjectsResponse.data.data) {
              const currentMemberProjects = Array.isArray(memberProjectsResponse.data.data)
                ? memberProjectsResponse.data.data
                : [];

              setMemberProjects(currentMemberProjects);

              const selectedProjects = [];

              currentMemberProjects.forEach((memberProject) => {
                const matchingProject = allProjects.find((project) => project.id === memberProject.projectId);

                if (matchingProject) {
                  selectedProjects.push(matchingProject);
                } else {
                  console.warn('Could not find matching project for projectId:', memberProject.projectId);
                }
              });

              setFormData((prev) => ({
                ...prev,
                selectedProjects,
              }));
            } else {
              setMemberProjects([]);
              setFormData((prev) => ({ ...prev, selectedProjects: [] }));
            }
          } catch (memberProjectError) {
            setMemberProjects([]);
            setFormData((prev) => ({ ...prev, selectedProjects: [] }));
          }
        } catch (error) {
          setProjects([]);
        } finally {
          setProjectsLoading(false);
        }
      }
    };

    loadProjectsAndMemberProjects();
  }, [activeWorkspace, member]);

  const isPrimaryOwner = formData.accountType === 'Primary owner';
  const disabledInputStyle = { borderStyle: 'solid', backgroundColor: '#f1f2f5', color: '#546077' };
  const departmentOptions = departments.length ? departments.map((d) => d.name) : FALLBACK_DEPARTMENTS;
  const designationOptions = designations.length ? designations.map((d) => d.name) : FALLBACK_DESIGNATIONS;

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c" data-testid="edit-member-modal">
      <Box style={{
        width: 600,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5" data-testid="modal-title">
          Edit Member
        </h5>
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>First Name</FieldLabel>
            <input
              name="firstName"
              type="text"
              value={formData?.firstName || ''}
              disabled
              className={TEXT_INPUT_CLASS}
              style={disabledInputStyle}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Last Name</FieldLabel>
            <input
              name="lastName"
              type="text"
              value={formData?.lastName || ''}
              disabled
              className={TEXT_INPUT_CLASS}
              style={disabledInputStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Email Address</FieldLabel>
            <input
              name="email"
              type="email"
              value={formData?.email || ''}
              disabled
              className={TEXT_INPUT_CLASS}
              style={disabledInputStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Account Type</FieldLabel>
            <select
              name="accountType"
              value={formData.accountType}
              onChange={handleForm}
              disabled={isPrimaryOwner}
              data-testid="account-type"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={isPrimaryOwner ? { ...STYLED_SELECT_STYLE, ...disabledInputStyle } : STYLED_SELECT_STYLE}
            >
              {isPrimaryOwner && <option value="Primary owner">Primary owner</option>}
              <option value="Team member">Team member</option>
              <option value="Team Leader">Team Leader</option>
              <option value="Workspace owner">Workspace owner</option>
              <option value="Workspace admin">Workspace admin</option>
            </select>
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Reporting To</FieldLabel>
            <select
              value={isPrimaryOwner ? '' : (formData.reportingManager || '')}
              onChange={(e) => setFormData({ ...formData, reportingManager: e.target.value ? Number(e.target.value) : '' })}
              disabled={isPrimaryOwner}
              data-testid="reporting-manager"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={isPrimaryOwner ? { ...STYLED_SELECT_STYLE, ...disabledInputStyle } : STYLED_SELECT_STYLE}
            >
              <option value="">Select a manager</option>
              {reportingManagers.filter((m) => m.id !== member.id).map((m) => (
                <option key={m.id} value={m.id}>{`${m.user.firstName} ${m.user.lastName}`}</option>
              ))}
            </select>
            {isPrimaryOwner && (
              <span className="block text-xs text-ink-500 mt-1">
                A Primary owner cannot be assigned a reporting manager.
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Department</FieldLabel>
            <select
              name="department"
              value={formData.department || ''}
              onChange={handleForm}
              data-testid="department"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="">Select department</option>
              {departmentOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Designation</FieldLabel>
            <select
              name="designation"
              value={formData.designation || ''}
              onChange={handleForm}
              data-testid="designation"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="">Select designation</option>
              {designationOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </Grid>

          <Grid item xs={12}>
            <ProjectSelector
              label="Projects"
              options={projects}
              selected={formData.selectedProjects}
              onChange={handleProjectSelection}
              placeholder={projects.length === 0 ? 'No projects available' : 'Select projects...'}
              loading={projectsLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <FieldLabel>Employment Start Date</FieldLabel>
            <DatePicker
              oneTap
              format="MM/dd/yyyy"
              name="employmentStartDate"
              value={formData.employmentStartDate}
              onChange={(_date) => setFormData({ ...formData, employmentStartDate: _date })}
              className="employment-start-date-picker"
              caretAs={CalendarCaret}
            />
          </Grid>
          <Grid item xs={12}>
            <PlacesAutocomplete
              location={formData.location}
              onChange={setFormData}
              textfieldType="outlined"
              label="Home Address"
              inlineLabel
              placeholder="Enter Street Address"
              textFieldSx={PLACES_SX}
            />
          </Grid>
        </Grid>

        <div className="flex justify-start items-center gap-2 mt-6">
          <button
            type="button"
            onClick={closeModal}
            data-testid="cancel"
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
            style={{ borderStyle: 'solid' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleEditMember}
            disabled={!isUpdatable() || isUpdating}
            data-testid="update-member"
            className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {isUpdating ? 'Updating…' : 'Update'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

EditMemberModal.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    accountType: PropTypes.string,
    department: PropTypes.string,
    designation: PropTypes.string,
    reportingManager: PropTypes.number,
    employmentStartDate: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      timezone: PropTypes.string,
      userLocation: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        formattedAddress: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  reportingManagers: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  departments: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  designations: PropTypes.array.isRequired,
};

export default EditMemberModal;
